import React, { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import Button from '../Button';
import { callable, callableFailure } from '../../data/useEntitlement';
import { unverifiedMessage } from '../../domain/clientEvents';
import { BRAND } from '../../domain/brand';
import s from './settings.module.css';

// Business Settings → Mobile app.
//
// The other half of the phone apps' sign-in, Android's and iPhone's alike:
// both scan this one code (ios/CodaStudio/UI/Screens/SignInView.swift sends the
// teacher here by name). The phone has no sign-up and no recovery, and scanning
// a code shown here is its primary way in — the email-and-password door is for
// store review (#725) — so a teacher in a lesson room with a new phone has to
// be able to find it; the heading names both phones (#634).
//
// Ours: MMS has no native app. The shape follows from the exchange
// (functions/devicelink/).
//
// ── Why the code is fetched on demand and not on mount ──────────────────────
//
// Minting one revokes the studio's previous code, and it lives two minutes.
// Minting on mount would spend a code every time somebody opened this tab, and
// would invalidate a code a teacher was scanning from another window. So: a
// button, then a countdown, then it dies and says so.

// The QR is drawn locally. The code never leaves this browser except to the
// phone's camera — sending it to an image service to be rendered would put a
// live credential in someone else's logs.
const QR_OPTIONS = { width: 260, margin: 1, errorCorrectionLevel: 'M' };

export default function MobileAppPanel() {
  const [state, setState] = useState('idle'); // idle | working | showing | expired | failed
  const [dataUrl, setDataUrl] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [failure, setFailure] = useState(null);

  // The minted code is held in a ref rather than in state on purpose: nothing
  // renders it as text, and keeping it out of the render tree keeps it out of
  // React DevTools and out of any future component that maps over state.
  const codeRef = useRef(null);

  const stop = useCallback(() => {
    codeRef.current = null;
    setDataUrl(null);
  }, []);

  const request = useCallback(async () => {
    const fn = callable('createDeviceLink');
    if (!fn) {
      setState('failed');
      setFailure('not-deployed');
      return;
    }
    setState('working');
    setFailure(null);
    try {
      const { data } = await fn();
      codeRef.current = data.code;
      setDataUrl(await QRCode.toDataURL(data.code, QR_OPTIONS));
      setSecondsLeft(Number(data.expiresInSeconds) || 0);
      setState('showing');
    } catch (err) {
      stop();
      setFailure(callableFailure(err));
      setState('failed');
    }
  }, [stop]);

  // The countdown is cosmetic — the server checks the expiry and refuses a
  // stale code whatever this says — but a QR that has silently stopped working
  // is worse than one that says it has.
  useEffect(() => {
    if (state !== 'showing') return undefined;
    if (secondsLeft <= 0) {
      stop();
      setState('expired');
      return undefined;
    }
    const t = setTimeout(() => setSecondsLeft((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [state, secondsLeft, stop]);

  // Leaving the tab drops the code rather than leaving one live on a screen
  // nobody is looking at.
  useEffect(() => stop, [stop]);

  return (
    <div className={s.panel}>
      <div className={s.panelTitle}>{BRAND.name} for iPhone and Android</div>

      <p className={s.blurb}>
        Install {BRAND.name} on your iPhone or Android phone, open it, and scan the code
        below. There is no password to type — signing in on the phone is this, and only
        this.
      </p>

      {/* The room for the code only once there is something to put in it:
          held open before the first code it would read as a broken image
          (#634). */}
      {state === 'idle' || state === 'working' ? null : (
        <div className={s.qrArea}>
          {state === 'showing' && dataUrl ? (
            <>
              {/* Empty alt, not a description: the image carries no information a
                  screen reader can act on — a camera has to read it — and
                  announcing "QR code" adds nothing the heading has not said. */}
              <img className={s.qr} src={dataUrl} alt="" width={260} height={260} />
              <p className={s.qrCaption} role="status">
                Expires in {secondsLeft}s
              </p>
            </>
          ) : null}

          {state === 'expired' ? (
            <p className={s.qrCaption}>That code has expired. Show another one.</p>
          ) : null}

          {state === 'failed' ? (
            <p className={s.qrCaption}>
              {failure === 'not-deployed'
                ? 'The mobile app is not switched on for this studio yet.'
                : failure === 'unverified'
                  ? unverifiedMessage()
                  : 'Could not create a sign-in code. Please try again.'}
            </p>
          ) : null}
        </div>
      )}

      <Button onClick={request} disabled={state === 'working'}>
        {state === 'working'
          ? 'Creating…'
          : state === 'showing'
            ? 'Show a new code'
            : 'Show sign-in code'}
      </Button>

      <p className={s.note}>
        A code works once and lasts two minutes. Showing a new one immediately stops
        the last. If you lose the phone, show a new code — the old one cannot be used
        again in any case.
      </p>
    </div>
  );
}
